import React, { useCallback, useRef, useState } from 'react';

/**
 * NimbusTable – standard data table shell used throughout the app.
 *
 * Renders:
 *   <div class="np-table-wrap">
 *     <table class="np-table">
 *       <thead>{thead}</thead>
 *       <tbody>{children | emptyState}</tbody>
 *     </table>
 *   </div>
 *
 * Usage:
 *   <NimbusTable thead={<tr><th>Name</th><th>Status</th></tr>} isEmpty={items.length === 0} emptyState={<div>No items</div>}>
 *     {items.map(item => <tr key={item.id}>...</tr>)}
 *   </NimbusTable>
 *
 * Columns are resizable by default: a drag handle on the right edge of each
 * <th> lets the user drag to resize, which pins that column (and every other
 * column, at its current rendered width) via a <colgroup> so the layout
 * doesn't jump around. Pass resizable={false} to opt out for a given table.
 */
interface NimbusTableProps {
  /** The <tr>…</tr> content for the header row */
  thead: React.ReactNode;
  /** <tr> rows for the body */
  children?: React.ReactNode;
  /** When true, renders emptyState spanning all columns instead of children */
  isEmpty?: boolean;
  /** Content shown when isEmpty is true */
  emptyState?: React.ReactNode;
  /** Extra classes appended to <table> (e.g. "table-bordered") */
  className?: string;
  /** Inline style on <table> – escape hatch for fixed-layout tables */
  style?: React.CSSProperties;
  /** Extra classes on the outer wrapper div */
  wrapperClassName?: string;
  /** Optional <col> elements for column widths (renders as <colgroup>) */
  colgroup?: React.ReactNode;
  /** Whether column headers get a drag-to-resize handle. Default true. */
  resizable?: boolean;
}

const MIN_COLUMN_WIDTH = 48;

export function NimbusTable({
  thead,
  children,
  isEmpty = false,
  emptyState,
  className,
  style,
  wrapperClassName,
  colgroup,
  resizable = true,
}: NimbusTableProps) {
  const tableRef = useRef<HTMLTableElement>(null);
  // Column widths in px, keyed by column index. Empty until the user resizes
  // something - up to that point the table lays itself out exactly as before.
  const [colWidths, setColWidths] = useState<Record<number, number>>({});
  const dragState = useRef<{ index: number; startX: number; startWidths: number[]; moved: boolean } | null>(null);
  // Many headers (e.g. sortable columns) have their own onClick - a resize
  // drag that happens to end with mouseup back over the <th> would otherwise
  // fire that click too. Swallow exactly one click after a real drag.
  const suppressNextClick = useRef(false);

  const onMouseMove = useCallback((e: MouseEvent) => {
    const drag = dragState.current;
    if (!drag) return;
    const delta = e.clientX - drag.startX;
    if (Math.abs(delta) > 2) drag.moved = true;
    const newWidth = Math.max(MIN_COLUMN_WIDTH, drag.startWidths[drag.index] + delta);
    setColWidths((prev) => ({ ...prev, [drag.index]: newWidth }));
  }, []);

  const stopDrag = useCallback(() => {
    if (dragState.current?.moved) {
      suppressNextClick.current = true;
    }
    dragState.current = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', stopDrag);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [onMouseMove]);

  const startDrag = useCallback((index: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const table = tableRef.current;
    if (!table) return;
    const headerCells = table.querySelectorAll('thead th');
    // Seed every column's width from its current rendered size, not just the
    // one being dragged - otherwise switching to fixed layout on the first
    // drag would let the browser re-flow every other column unpredictably.
    const startWidths = Array.from(headerCells).map((th) => (th as HTMLElement).getBoundingClientRect().width);
    dragState.current = { index, startX: e.clientX, startWidths, moved: false };
    setColWidths((prev) => {
      const next = { ...prev };
      startWidths.forEach((w, i) => {
        if (next[i] === undefined) next[i] = w;
      });
      return next;
    });
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', stopDrag);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [onMouseMove, stopDrag]);

  const swallowClickIfJustDragged = useCallback((e: React.MouseEvent) => {
    if (suppressNextClick.current) {
      suppressNextClick.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const hasResizedAnyColumn = Object.keys(colWidths).length > 0;

  let renderedThead = thead;
  let columnCount = 0;

  if (resizable && React.isValidElement(thead) && thead.type === 'tr') {
    const headerRow = thead as React.ReactElement<{ children?: React.ReactNode }>;
    const cells = React.Children.toArray(headerRow.props.children);
    columnCount = cells.length;
    renderedThead = React.cloneElement(headerRow, {
      children: cells.map((cell, index) => {
        if (!React.isValidElement(cell)) return cell;
        const cellProps = cell.props as { style?: React.CSSProperties; children?: React.ReactNode };
        const width = colWidths[index];
        return React.cloneElement(cell, {
          key: cell.key ?? index,
          onClickCapture: swallowClickIfJustDragged,
          style: {
            ...cellProps.style,
            ...(width !== undefined ? { width, maxWidth: width } : null),
            position: 'relative',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          children: (
            <>
              {cellProps.children}
              <span
                onMouseDown={(e) => startDrag(index, e)}
                title="Drag to resize column"
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: '6px',
                  cursor: 'col-resize',
                  userSelect: 'none',
                  zIndex: 1,
                }}
                className="nimbus-col-resize-handle"
              />
            </>
          ),
        } as any);
      }),
    } as any);
  }

  return (
    <div className={`np-table-wrap${wrapperClassName ? ` ${wrapperClassName}` : ''}`}>
      <style>{`
        .nimbus-col-resize-handle:hover,
        .nimbus-col-resize-handle:active {
          background: var(--bs-primary);
          opacity: 0.4;
        }
      `}</style>
      <table
        ref={tableRef}
        className={`np-table${className ? ` ${className}` : ''}`}
        style={{ ...style, ...(hasResizedAnyColumn ? { tableLayout: 'fixed' } : null) }}
      >
        {colgroup && <colgroup>{colgroup}</colgroup>}
        {!colgroup && hasResizedAnyColumn && (
          <colgroup>
            {Array.from({ length: columnCount }, (_, i) => (
              <col key={i} style={colWidths[i] !== undefined ? { width: colWidths[i] } : undefined} />
            ))}
          </colgroup>
        )}
        <thead>
          {renderedThead}
        </thead>
        <tbody>
          {isEmpty && emptyState != null ? (
            <tr>
              <td colSpan={100} className="p-0">
                {emptyState}
              </td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}

export default NimbusTable;
